import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiGitBranch, 
  FiGitCommit, 
  FiGitMerge, 
  FiUser, 
  FiCalendar,
  FiMessageSquare,
  FiChevronDown,
  FiChevronRight,
  FiPlus,
  FiRefreshCw,
  FiDownload
} from 'react-icons/fi';

const VersionControl = ({ versions, files }) => {
  const [expandedVersions, setExpandedVersions] = useState(new Set());
  const [showCreateVersion, setShowCreateVersion] = useState(false);

  const toggleVersionExpansion = (versionId) => {
    const newExpanded = new Set(expandedVersions);
    if (newExpanded.has(versionId)) {
      newExpanded.delete(versionId);
    } else {
      newExpanded.add(versionId);
    }
    setExpandedVersions(newExpanded);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getChangeIcon = (type) => {
    switch (type) {
      case 'add':
        return <span className="text-success-600">+</span>;
      case 'modify':
        return <span className="text-warning-600">~</span>;
      case 'delete':
        return <span className="text-error-600">-</span>;
      default:
        return <span className="text-secondary-600">•</span>;
    }
  };

  const getChangeColor = (type) => {
    switch (type) {
      case 'add':
        return 'bg-success-50 border-success-200 text-success-800';
      case 'modify':
        return 'bg-warning-50 border-warning-200 text-warning-800';
      case 'delete':
        return 'bg-error-50 border-error-200 text-error-800';
      default:
        return 'bg-secondary-50 border-secondary-200 text-secondary-800';
    }
  };

  // Mock version data for demonstration
  const mockVersions = [
    {
      id: 'v1.0.0',
      fileId: 'inspection_config.json',
      version: '2026.01.29',
      timestamp: new Date('2026-01-29T10:00:00'),
      author: 'System Admin',
      comment: 'Initial version with complete inspection configuration',
      changes: [
        { type: 'add', description: 'Added tank inspection configuration' },
        { type: 'add', description: 'Added tank dimensions and location data' }
      ]
    },
    {
      id: 'v1.0.1',
      fileId: 'bottom_dataset1_events.txt',
      version: '2026.01.30',
      timestamp: new Date('2026-01-30T14:30:00'),
      author: 'Data Analyst',
      comment: 'Updated event data with corrected coordinates',
      changes: [
        { type: 'modify', description: 'Fixed coordinate transformation for events 2-5' },
        { type: 'modify', description: 'Updated amplitude values for better accuracy' }
      ]
    },
    {
      id: 'v1.1.0',
      fileId: 'history_metadata.json',
      version: '2026.02.01',
      timestamp: new Date('2026-02-01T09:15:00'),
      author: 'Inspector',
      comment: 'Added new inspection history entry',
      changes: [
        { type: 'add', description: 'Added vacuum box inspection record' },
        { type: 'modify', description: 'Updated texture layer priorities' }
      ]
    }
  ];

  const allVersions = versions.length > 0 ? versions : mockVersions;

  const renderVersionItem = (version, index) => {
    const isExpanded = expandedVersions.has(version.id);

    return (
      <motion.div
        key={version.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1 }}
        className="bg-white rounded-xl shadow-soft border border-secondary-100 overflow-hidden"
      >
        <div
          className="flex items-center px-6 py-4 cursor-pointer hover:bg-secondary-50"
          onClick={() => toggleVersionExpansion(version.id)}
        >
          <div className="flex items-center flex-1">
            <FiGitCommit className="w-5 h-5 text-primary-500 mr-3" />
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-secondary-800">{version.version}</span>
                <span className="text-xs text-secondary-500 bg-secondary-100 px-2 py-1 rounded">
                  {version.fileId}
                </span>
              </div>
              <div className="text-sm text-secondary-600 mt-1">{version.comment}</div>
              <div className="flex items-center space-x-4 mt-2 text-xs text-secondary-500">
                <div className="flex items-center space-x-1">
                  <FiUser className="w-3 h-3" />
                  <span>{version.author}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <FiCalendar className="w-3 h-3" />
                  <span>{formatDate(version.timestamp)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <FiMessageSquare className="w-3 h-3" />
                  <span>{version.changes.length} change{version.changes.length > 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {isExpanded ? (
              <FiChevronDown className="w-4 h-4 text-secondary-500" />
            ) : (
              <FiChevronRight className="w-4 h-4 text-secondary-500" />
            )}
          </div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t border-secondary-100"
            >
              <div className="p-6 bg-secondary-50">
                <h4 className="font-medium text-secondary-800 mb-4">Changes</h4>
                <div className="space-y-2">
                  {version.changes.map((change, changeIndex) => (
                    <div
                      key={changeIndex}
                      className={`flex items-center space-x-3 p-3 rounded-lg border ${getChangeColor(
                        change.type
                      )}`}
                    >
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center text-sm font-medium">
                        {getChangeIcon(change.type)}
                      </div>
                      <span className="text-sm">{change.description}</span>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-secondary-200">
                  <div className="text-xs text-secondary-500">
                    Version ID: {version.id}
                  </div>
                  <button className="btn-outline text-sm flex items-center space-x-1">
                    <FiDownload className="w-3 h-3" />
                    <span>Download</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  return (
    <div className="h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-secondary-800 mb-2">Version Control</h2>
        <p className="text-secondary-600">Track changes and manage file versions</p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between mb-6">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowCreateVersion(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus />
          <span>Create Version</span>
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-outline flex items-center space-x-2"
        >
          <FiRefreshCw />
          <span>Refresh</span>
        </motion.button>
      </div>

      {/* Version Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Total Versions</p>
              <p className="text-2xl font-bold text-secondary-800">{allVersions.length}</p>
            </div>
            <FiGitBranch className="w-8 h-8 text-primary-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Files Tracked</p>
              <p className="text-2xl font-bold text-secondary-800">{files.length}</p>
            </div>
            <FiGitCommit className="w-8 h-8 text-secondary-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-500">Latest Version</p>
              <p className="text-lg font-bold text-secondary-800">
                {allVersions.length > 0 ? allVersions[0].version : 'N/A'}
              </p>
            </div>
            <FiGitMerge className="w-8 h-8 text-accent-500" />
          </div>
        </motion.div>
      </div>

      {/* Version List */}
      <div className="space-y-4">
        <h3 className="font-semibold text-secondary-800">Version History</h3>
        {allVersions.map((version, index) => renderVersionItem(version, index))}
      </div>

      {/* Empty State */}
      {allVersions.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-soft border border-secondary-100 p-12 text-center"
        >
          <FiGitBranch className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary-800 mb-2">No Version History</h3>
          <p className="text-secondary-600 mb-6">
            Start tracking changes to your data files by creating your first version
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowCreateVersion(true)}
            className="btn-primary"
          >
            Create First Version
          </motion.button>
        </motion.div>
      )}

      {/* Create Version Modal */}
      <AnimatePresence>
        {showCreateVersion && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="theme-modal-backdrop fixed inset-0 flex items-center justify-center z-50"
            onClick={() => setShowCreateVersion(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              className="theme-modal rounded-xl shadow-strong p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-secondary-800 mb-4">Create New Version</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Version
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g., 2026.02.21"
                    defaultValue={new Date().toISOString().split('T')[0].replace(/-/g, '.')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Comment
                  </label>
                  <textarea
                    className="input-field"
                    rows={3}
                    placeholder="Describe the changes in this version..."
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <button
                    onClick={() => setShowCreateVersion(false)}
                    className="btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowCreateVersion(false)}
                    className="btn-primary"
                  >
                    Create Version
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VersionControl;
